import { Link, useLocation } from "react-router-dom";
import plusIcon from "../assets/icon/icon-plus.png";
import routes from "../routes";
import { useAuth } from "../routers/AuthProvider";
import { useEffect, useState } from "react";

const normalizePath = (path) => {
  if (!path) return "";
  return path.replace(/\/:\w+/g, "");
};

const childrenPath = (route) => {
  if (!route) return "#";
  if (route.path) {
    return route.path;
  }
  if (route.children && route.children.length > 0) {
    return childrenPath(route.children[0]);
  }
  return "#";
};

const getRoutePaths = (route) => {
  if (!route) return [];
  if (route.path) {
    return [route.path];
  }
  if (!route.children) {
    return [];
  }
  return route.children.flatMap((child) => getRoutePaths(child));
};

const matchesPath = (currentPath, routePath) => {
  const basePath = normalizePath(routePath);
  return (
    !!basePath &&
    (currentPath === basePath || currentPath.startsWith(`${basePath}/`))
  );
};

const isRouteActive = (currentPath, route) => {
  return getRoutePaths(route).some((routePath) => matchesPath(currentPath, routePath));
};

export function Sidebar() {
  const { user } = useAuth();
  const [sidebarComponents, setSidebarComponents] = useState(null);

  // Permission check helper
  const allow = (permissions, moduleOrSubModule) => {
    return permissions.some((perm) => {
      const hasAccess =
        perm.view == 1 || perm.edit == 1 || perm.write == 1 || perm.delete == 1;

      if (perm.sub_module_name && perm.sub_module_name == moduleOrSubModule) {
        return hasAccess;
      }

      if (!perm.sub_module_name && perm.module_name == moduleOrSubModule) {
        return hasAccess;
      }

      return false;
    });
  };

  useEffect(() => {
    if (!user || !user.role || !user.role.permissions) return;

    const tempSidebar = routes.map((route) => {
      if (route.collapse) {
        // Filter children based on permission
        const allowedChildren = route.children.filter((child) =>
          allow(user.role.permissions, child.permissionName)
        );

        if (allowedChildren.length === 0) {
          return null; // hide parent if no child permission
        }

        // Return parent with filtered children
        return (
          <NestedNavItem
            key={route.key}
            route={{ ...route, children: allowedChildren }}
          />
        );
      } else {
        // Single route permission check
        if (allow(user.role.permissions, route.permissionName)) {
          return <NavItem key={route.key} route={route} />;
        } else {
          return null;
        }
      }
    });

    setSidebarComponents(tempSidebar);
  }, [user]);

  return <div className="navigation">{sidebarComponents}</div>;
}

const NestedNavItem = ({ route }) => {
  const { key, icon, title, children } = route;
  const location = useLocation();
  const currentPath = location.pathname;
  const active = isRouteActive(currentPath, route);

  const getFirstChildRoute = (childR) => {
    if (!childR.path && childR.children) {
      return getFirstChildRoute(childR.children[0]);
    } else {
      return childR;
    }
  };

  return (
    <div className="nested-nav-item">
      <Link
        to={children[0].path ?? childrenPath(children[0])}
        key={key}
        className={`parent-nav ${active ? "active" : ""}`}
      >
        <img alt="sidebar icon" src={icon} />
        <span>{title}</span>
        <img alt="downarrow" src={plusIcon} className="downarrow" />
      </Link>
      {active && (
        <div className="child-nav">
          {children.map((childRoute) => {
            if (childRoute.collapse) {
              return <NestedNavItem key={childRoute.key} route={childRoute} />;
            } else if (!childRoute.path && childRoute.children) {
              return (
                <NavItem
                  key={childRoute.key}
                  route={getFirstChildRoute(childRoute)}
                  child={true}
                />
              );
            } else {
              return (
                <NavItem key={childRoute.key} route={childRoute} child={true} />
              );
            }
          })}
        </div>
      )}
    </div>
  );
};

const NavItem = ({ route, child = false }) => {
  const { key, icon, title, path } = route;
  const location = useLocation();
  const currentPath = location.pathname;
  const active = isRouteActive(currentPath, route);

  return (
    <Link
      key={key}
      to={path ?? childrenPath(route.children ? route.children[0] : {})}
      className={`${child ? "sub-nav-item" : "nav-item"} ${
        active ? "active" : ""
      }`}
    >
      {!child && <img alt="sidebar icon" src={icon} />}
      <span>{title}</span>
    </Link>
  );
};
